import React from 'react';

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 30, 40, 50],
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (totalItems === 0) {
    return null;
  }

  return (
    <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      {/* Total Items - Left */}
      <div className="text-[12px] text-gray-700 text-center sm:text-left">
        Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
        {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{' '}
        results
      </div>

      {/* Page Navigation - Center */}
      <div className="flex items-center justify-center space-x-1">
        <button
          className="px-2 py-1 text-[12px] border border-gray-300 rounded-md hover:bg-white hover:text-black disabled:opacity-50"
          disabled={currentPage === 1}
          onClick={() => {
            if (currentPage > 1) {
              onPageChange(1);
            }
          }}
        >
          <i className="ri-arrow-left-double-line text-[10px]"></i>
        </button>
        <button
          className="px-2 py-1 text-[12px] border border-gray-300 rounded-md hover:bg-white hover:text-black disabled:opacity-50"
          disabled={currentPage === 1}
          onClick={() => {
            if (currentPage > 1) {
              onPageChange(currentPage - 1);
            }
          }}
        >
          <i className="ri-arrow-left-s-line text-[10px]"></i>
        </button>

        {(() => {
          const maxVisiblePages = 3;
          let startPage = Math.max(
            1,
            currentPage - Math.floor(maxVisiblePages / 2)
          );
          let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

          if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
          }

          const pages = [];
          for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
          }

          return pages.map((page) => (
            <button
              key={page}
              className={`px-3 py-1 text-[12px] border rounded-md hover:bg-gray-50 ${
                currentPage === page
                  ? 'border-primary bg-primary text-white hover:text-black'
                  : 'border-gray-300 text-black'
              }`}
              onClick={() => onPageChange(page)}
            >
              {page}
            </button>
          ));
        })()}

        <button
          className="px-2 py-1 text-[12px] border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          disabled={currentPage === totalPages}
          onClick={() => {
            if (currentPage < totalPages) {
              onPageChange(currentPage + 1);
            }
          }}
        >
          <i className="ri-arrow-right-s-line text-[10px]"></i>
        </button>
        <button
          className="px-2 py-1 text-[12px] border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          disabled={currentPage === totalPages}
          onClick={() => {
            if (currentPage < totalPages) {
              onPageChange(totalPages);
            }
          }}
        >
          <i className="ri-arrow-right-double-line text-[10px]"></i>
        </button>
      </div>

      {/* Page Size Selector - Right */}
      <div className="flex items-center justify-center sm:justify-end space-x-2">
        {onPageSizeChange && (
          <>
            <span className="text-[12px] text-gray-700">Show:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="text-[12px] border border-gray-300 rounded-md px-2 py-1 bg-white hover:border-gray-400 focus:outline-none focus:border-primary"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span className="text-[12px] text-gray-700">/ page</span>
          </>
        )}
      </div>
    </div>
  );
};

export default Pagination;
